import React, { useMemo } from 'react';
import styled from 'styled-components';
import dayjs from 'dayjs';

import { useCollection } from '@nandorojo/swr-firestore';
import { IcalendarItem } from '@typings/calendar/calendar.interface';
import CalendrItem from './CalendrItem';
import { RangePosition } from '@utils/calendar/rangeEventUtils';

const ListWrap = styled.div`
  display: flex;
  gap: 2px;
  flex-direction: column;
`;

// robust Dayjs parser for Firestore Timestamp/string/Date
function toDayjsDate(value: any): dayjs.Dayjs {
  if (!value) return dayjs('invalid');
  if (typeof value?.toDate === 'function') return dayjs(value.toDate());
  if (value instanceof Date) return dayjs(value);
  if (dayjs.isDayjs(value)) return value as dayjs.Dayjs;
  return dayjs(value);
}

type Props = {
  dateQuery: any;
  collectionPath?: string;
};
function CalenderList({ dateQuery, collectionPath }: Props) {
  const _collectionPath = collectionPath || 'calendar';
  const currentDate = dayjs(dateQuery);

  // 주 경계 포함 월 뷰 범위
  const firstOfMonth = currentDate.startOf('month');
  const lastOfMonth = currentDate.endOf('month');
  const viewStart = firstOfMonth.subtract(firstOfMonth.day(), 'day');
  const viewEnd = lastOfMonth.add(6 - lastOfMonth.day(), 'day');
  const monthEnd = viewEnd.format('YYYY-MM-DD');

  // 연속 일정 지원: 최대 1년 전부터 이번 달 종료일까지 범위 쿼리
  const monthStart = viewStart.subtract(1, 'year').format('YYYY-MM-DD');
  const { data: monthlyItems } = useCollection<IcalendarItem>(_collectionPath, {
    listen: true,
    where: [
      ['date', '>=', monthStart],
      ['date', '<=', monthEnd],
    ],
    orderBy: ['date', 'asc'],
  });

  const getRangePositionForDay = (
    current: dayjs.Dayjs,
    from: dayjs.Dayjs,
    to: dayjs.Dayjs,
  ): RangePosition => {
    if (from.isSame(to, 'day')) return 'single';

    const isFirst = current.isSame(from, 'day');
    const isLast = current.isSame(to, 'day');
    const isWeekStart = current.day() === 0;
    const isWeekEnd = current.day() === 6;

    if (isFirst && isLast) return 'single';
    if (isLast && !isWeekStart) return 'end';
    if (!isLast && isWeekEnd) return 'end';
    if (isFirst && !isWeekEnd) return 'start';
    if (!isFirst && isWeekStart) return 'start';
    return 'middle';
  };

  // 🔧 현재 월 뷰 범위에 걸치는 일정만 먼저 필터링 (성능 최적화)
  const itemsInViewRange = useMemo(() => {
    const items = monthlyItems ?? [];
    const viewStartDate = viewStart;
    const viewEndDate = viewEnd;

    return items.filter((item) => {
      const from = dayjs(item.date, 'YYYY-MM-DD');
      const _to = item.toDate ? toDayjsDate(item.toDate) : from;
      const to = _to.isValid() ? _to : from;

      // 일정이 현재 뷰 범위와 겹치는지 확인
      return (
        (to.isAfter(viewStartDate, 'day') || to.isSame(viewStartDate, 'day')) &&
        (from.isBefore(viewEndDate, 'day') || from.isSame(viewEndDate, 'day'))
      );
    });
  }, [monthlyItems, viewStart, viewEnd]);

  const itemsForDay = useMemo(() => {
    const current = dayjs(dateQuery);

    return itemsInViewRange
      .filter((item) => {
        const from = dayjs(item.date, 'YYYY-MM-DD');
        const _to = item.toDate ? toDayjsDate(item.toDate) : from;
        const to = _to.isValid() ? _to : from;
        return (
          current.isSame(from, 'day') ||
          current.isSame(to, 'day') ||
          (current.isAfter(from, 'day') && current.isBefore(to, 'day'))
        );
      })
      .sort(
        (a, b) =>
          toDayjsDate(a.createdAt).valueOf() -
          toDayjsDate(b.createdAt).valueOf(),
      );
  }, [itemsInViewRange, dateQuery]);

  return (
    <ListWrap>
      {itemsForDay.map((item) => {
        const from = dayjs(item.date, 'YYYY-MM-DD');
        const _to = item.toDate ? toDayjsDate(item.toDate) : from;
        const to = _to.isValid() ? _to : from;
        const rangePosition = getRangePositionForDay(
          dayjs(dateQuery),
          from,
          to,
        );
        return (
          <CalendrItem
            key={item.id}
            data={item}
            rangePosition={rangePosition}
          />
        );
      })}
    </ListWrap>
  );
}

export default CalenderList;
